import React, { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import "./../../../../assets/css/purchase/form.css";

// React DateTime
import Datetime from "react-datetime";
import moment from "moment";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import ApModal from "../../../components/Modal/ApModal";
import FormAction from "../../../components/Actions/FormAction";
import ItemWhTable from "../../../components/Content/ItemWhTable";

// Model
import { whDocMasterModel } from "../../../../model/Warehouse/WHDocMasterModel";
import { whDocDetailModel } from "../../../../model/Warehouse/WHDocDetailModel";

import {
  getDataWithComp,
  getViewAp,
  formatCurrency,
  getAlert,
  getMaxAdjNo,
  randomDelay,
  parseCurrency,
  getDataWithJoin,
} from "../../../../utils/SamuiUtils";

import {
  formatDateTime,
  formatDateOnChange,
} from "../../../../utils/DateUtils";

import { manageWhItemData } from "../../../../utils/WarehouseUtils";

const normalizeToSqlDateTime = (input, defaultTime = "start") => {
  if (!input) return null;

  const applyTime = (m) => {
    if (defaultTime === "end") {
      return m.clone().hour(23).minute(59).second(59).millisecond(0);
    }
    return m.clone().hour(0).minute(0).second(0).millisecond(0);
  };

  let m = null;
  if (moment.isMoment(input)) {
    m = input.clone();
  } else if (input instanceof Date) {
    m = moment(input);
  } else if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // รองรับรูปแบบ UI ไทย เช่น 10-04-2569 หรือ 10-04-2569 14:35:00
    const thaiMatch = trimmed.match(
      /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (thaiMatch) {
      const [, dd, mm, yyyy, hh, min, ss] = thaiMatch;
      const yearCE = Number(yyyy) > 2400 ? Number(yyyy) - 543 : Number(yyyy);
      m = moment(
        `${yearCE}-${mm}-${dd} ${hh ?? "00"}:${min ?? "00"}:${ss ?? "00"}`,
        "YYYY-MM-DD HH:mm:ss",
        true
      );
    } else {
      m = moment(
        trimmed,
        [
          "YYYY-MM-DD HH:mm:ss.SSS",
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD",
          moment.ISO_8601,
        ],
        true
      );
      if (!m.isValid()) m = moment(trimmed);
    }
  }

  if (!m || !m.isValid()) return null;
  const withTime =
    typeof input === "string" && input.includes(":") ? m : applyTime(m);
  return withTime.format("YYYY-MM-DD HH:mm:ss.SSS");
};

const FormAdjust = ({ callInitialize, mode, name, whDocNo }) => {
  const navigate = useNavigate();
  const [formMasterList, setFormMasterList] = useState(whDocMasterModel());
  const [formDetailList, setFormDetailList] = useState([]);
  const [itemDataList, setItemDataList] = useState([]);
  const [whDataList, setWhDataList] = useState([]);
  const [apDataList, setApDataList] = useState([]);
  const [allOnhandData, setAllOnhandData] = useState([]);
  const [latestWhPerItem, setLatestWhPerItem] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);

  // การเลือกคลังจากหน้าจอ
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    const whDataList = await getDataWithComp(
      "Tb_Set_WH",
      ` AND WH_Status = 'Y' ORDER BY WH_Name ASC`
    );
    if (whDataList && whDataList.length > 0) {
      setWhDataList(whDataList);
    }

    const apDataList = await getViewAp();
    if (apDataList && apDataList.length > 0) {
      setApDataList(apDataList);
    }

    // Load all items without warehouse filter (onhand shown per-row after warehouse selected)
    await loadAllItems();

    // Load all item-warehouse onhand for per-row warehouse filtering
    const onhandAll = await getDataWithComp("API_1101_WH_ITEM_ONHAND", " ORDER BY Item_Id ASC");
    setAllOnhandData(onhandAll || []);

    // Load latest warehouse per item from WH_ITEM_STC (stock transaction history)
    const historyData = await getDataWithComp(
      "WH_ITEM_STC",
      "AND WH_Id IS NOT NULL ORDER BY STC_SEQ DESC"
    );
    const latestMap = {};
    for (const row of historyData || []) {
      const itemId = Number(row.Item_Id);
      if (!latestMap[itemId]) {
        latestMap[itemId] = Number(row.WH_Id);
      }
    }
    setLatestWhPerItem(latestMap);
  };

  const loadAllItems = async () => {
    const companyId = localStorage.getItem("company");
    try {
      const data = await getDataWithJoin(
        `
        v.Item_Id, v.Item_Barcode, v.Item_Code,
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name,
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT,
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB,
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL((SELECT SUM(wh.Item_Onhand) FROM API_1101_WH_ITEM_ONHAND wh WHERE wh.Item_Id = v.Item_Id AND wh.Comp_Id = ${companyId}), 0) AS Item_Onhand
      `,
        `View_Set_Item v`,
        `LEFT JOIN Tb_Set_ItmGroup grp ON (grp.Group_Id = v.Item_Group AND grp.Comp_Id = ${companyId} AND grp.Group_Code = 'G')`,
        `WHERE v.Comp_Id = ${companyId} AND v.Item_Status = 'Y'
         ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line`
      );
      if (data && data.length > 0) setItemDataList(data);
    } catch (error) {
      console.error("Error loading items:", error);
    }
  };

  const handleChangeMaster = (e) => {
    const { name, value } = e.target;
    // อัปเดตค่าใน formMasterList
    setFormMasterList((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ฟังก์ชันแปลงวันที่จากรูปแบบต่างๆ เป็นรูปแบบ dd-mm-yyyy (พ.ศ.)
  const formatDateToThai = (dateString) => {
    if (!dateString) return '';
    
    // ถ้าเป็นรูปแบบ dd-mm-yyyy (พ.ศ.) อยู่แล้ว ให้ return ตามเดิม
    if (typeof dateString === 'string') {
      const thaiDatePattern = /^\d{2}-\d{2}-\d{4}$/;
      if (thaiDatePattern.test(dateString)) {
        const parts = dateString.split('-');
        const year = parseInt(parts[2], 10);
        // ถ้าเป็นปีพ.ศ. (มากกว่า 2400) ให้ return ตามเดิม
        if (year > 2400) {
          return dateString;
        }
      }
    }
    
    // แปลงจาก datetime format หรือรูปแบบอื่นๆ
    const date = moment(dateString);
    if (!date.isValid()) return '';
    
    const day = date.date();
    const month = date.month() + 1; // moment ใช้ month 0-11
    const year = date.year() + 543; // แปลงเป็นพ.ศ.
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
  };

  // ฟังก์ชันแปลงวันที่จากรูปแบบ dd-mm-yyyy (พ.ศ.) เป็น moment object (ค.ศ.) สำหรับ react-datetime
  const parseThaiDateToMoment = (dateString) => {
    if (!dateString) return null;
    // ถ้าเป็น moment object อยู่แล้ว ให้ return ตามเดิม
    if (moment.isMoment(dateString)) return dateString;
    
    // แปลงเป็นรูปแบบไทยก่อน
    const thaiDate = formatDateToThai(dateString);
    
    // ถ้าเป็น string ในรูปแบบ dd-mm-yyyy (พ.ศ.)
    if (typeof thaiDate === 'string' && thaiDate) {
      const parts = thaiDate.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // moment ใช้ month 0-11
        const buddhistYear = parseInt(parts[2], 10);
        const christianYear = buddhistYear - 543;
        return moment({ year: christianYear, month: month, date: day });
      }
    }
    
    // ถ้าไม่ใช่รูปแบบที่คาดหวัง ให้ใช้ moment parse ตามปกติ
    return moment(dateString);
  };

  // ฟังก์ชันแปลง moment object (ค.ศ.) เป็น string dd-mm-yyyy (พ.ศ.)
  const formatMomentToThaiDate = (momentObj) => {
    if (!momentObj || !moment.isMoment(momentObj) || !momentObj.isValid()) {
      return '';
    }
    const day = momentObj.date();
    const month = momentObj.month() + 1; // moment ใช้ month 0-11
    const year = momentObj.year() + 543; // แปลงเป็นพ.ศ.
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
  };

  const formatNumberInput = (raw) => {
    if (raw === null || raw === undefined) return "";
    const cleaned = String(raw).replace(/,/g, "");
    if (cleaned === "") return "";
    if (!/^\d*\.?\d*$/.test(cleaned)) return String(raw);
    const [intPart = "", decPart] = cleaned.split(".");
    const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (decPart !== undefined) {
      const decTrim = decPart.slice(0, 2);
      return `${intFmt}.${decTrim}`;
    }
    return intFmt;
  };

  const handleChangeDateMaster = (value, name) => {
    // value จาก react-datetime จะเป็น moment object (ค.ศ.)
    if (value && moment.isMoment(value)) {
      // แปลงเป็นรูปแบบ dd-mm-yyyy (พ.ศ.)
      const thaiDate = formatMomentToThaiDate(value);
      setFormMasterList((prev) => ({
        ...prev,
        [name]: thaiDate,
      }));
    } else {
      // ถ้าไม่ใช่ moment object ใช้ formatDateOnChange ตามเดิม
      const newValue = value ? value.format("YYYY-MM-DD") : value;
      setFormMasterList((prev) => ({
        ...prev,
        [name]: formatDateOnChange(newValue),
      }));
    }
  };

  // const handleChangeWarehouse = async (value) => {
  //   // เคลียค่าตอนกดย้ายคลัง
  //   setFormDetailList([]);

  //   setItemDataList([]);

  //   const itemDataList = await getDataWithComp(
  //     "API_1101_WH_ITEM_ONHAND",
  //     `AND WH_Id = ${parseCurrency(value)} ORDER BY Item_Code ASC`
  //   );
  //   if (itemDataList && itemDataList.length > 0) {
  //     setItemDataList(itemDataList);
  //   }

  //   setSelectedWarehouse(value);
  // };

  const handleChangeWarehouse = async (value) => {
    setFormDetailList([]);
    setItemDataList([]);

    const whId = parseCurrency(value);
    const companyId = localStorage.getItem("company");

    console.debug("🔍 Loading warehouse items:", { whId, companyId });

    try {
      const data = await getDataWithJoin(
        `
        v.Item_Id, v.Item_Barcode, v.Item_Code, 
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name, 
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT, 
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB, 
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL(wh.Item_Onhand, 0) AS Item_Onhand
      `,
        `View_Set_Item v`,
        `
        LEFT JOIN Tb_Set_ItmGroup grp ON (grp.Group_Id = v.Item_Group AND grp.Comp_Id = ${companyId} AND grp.Group_Code = 'G')
        ${
          whId
            ? `LEFT JOIN (
          SELECT wh.*, ROW_NUMBER() OVER (PARTITION BY wh.Item_Id ORDER BY wh.Item_Onhand DESC) AS rn
          FROM API_1101_WH_ITEM_ONHAND wh
          WHERE wh.WH_Id = ${whId} AND wh.Comp_Id = ${companyId}
        ) wh ON (wh.Item_Id = v.Item_Id AND wh.rn = 1)`
            : ""
        }
      `,
        `
        WHERE v.Comp_Id = ${companyId} 
        AND v.Item_Status = 'Y'
        ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line
      `
      );

      // console.debug(" Data loaded:", data?.length, "items");
      // console.debug("Sample item:", data?.[0]);

      if (data && data.length > 0) {
        setItemDataList(data);
      } else {
        console.warn("⚠️ No items found for warehouse:", whId);
      }
    } catch (error) {
      console.error("❌ Error loading warehouse items:", error);
      alert(`เกิดข้อผิดพลาด: ${error.message || error}`);
    }

    setSelectedWarehouse(value);
  };

  const handleGlobalWarehouseChange = (whId) => {
    const parsedWhId = whId ? Number(whId) : null;
    setSelectedWarehouse(whId);
    if (formDetailList.length === 0) return;
    setFormDetailList((prev) =>
      prev.map((item) => {
        const onhandEntry = parsedWhId
          ? allOnhandData.find(
              (oh) => Number(oh.Item_Id) === Number(item.itemId) && Number(oh.WH_Id) === parsedWhId
            )
          : null;
        const onhand = onhandEntry ? parseCurrency(onhandEntry.Item_Onhand) || 0 : 0;
        return {
          ...item,
          fWhId: parsedWhId,
          itemOnHand: onhand,
          itemBalance: onhand,
          itemIncrease: '0.00',
          itemDecrease: '0.00',
          itemQty: 0,
          docType: null,
        };
      })
    );
  };

  const handleWarehouseChangePerRow = async (index, whId) => {
    const updatedList = [...formDetailList];
    const parsedWhId = whId ? Number(whId) : null;
    updatedList[index] = { ...updatedList[index], fWhId: parsedWhId };

    if (parsedWhId) {
      try {
        const onhandData = await getDataWithComp(
          "API_1101_WH_ITEM_ONHAND",
          `AND WH_Id = ${parsedWhId} AND Item_Id = ${updatedList[index].itemId}`
        );
        const onhand =
          onhandData && onhandData.length > 0
            ? parseCurrency(onhandData[0].Item_Onhand) || 0
            : 0;
        updatedList[index].itemOnHand = onhand;
        updatedList[index].itemBalance = onhand;
      } catch (e) {
        updatedList[index].itemOnHand = 0;
        updatedList[index].itemBalance = 0;
      }
    } else {
      updatedList[index].itemOnHand = 0;
      updatedList[index].itemBalance = 0;
    }

    setFormDetailList(updatedList);
  };

  const handleQuantityChange = (index, type, value, onHand) => {
    const rawValue = String(value ?? "").replace(/,/g, "");
    // ตรวจสอบว่าค่าที่กรอกเข้ามาเป็นตัวเลขหรือตัวเลขที่มีจุดทศนิยม (รองรับ comma)
    if (!/^\d*\.?\d*$/.test(rawValue)) {
      //getAlert("FAILED", "กรุณากรอกเฉพาะตัวเลขเท่านั้น");
      return;
    }

    const updatedList = [...formDetailList];

    // หยอดข้อมูล เพิ่ม/ลด รายละเอียดสินค้า แยกตามประเภท
    if (type === "increase") {
      updatedList[index].itemIncrease = value;
      updatedList[index].itemDecrease = null;
      updatedList[index].itemBalance =
        parseCurrency(onHand) + parseCurrency(value);
      updatedList[index].itemQty = parseCurrency(value);
    } else if (type === "decrease") {
      // ดักไม่ให้ปรับปรุงเกินกว่าสินค้าคงเหลือ เฉพาะตอนปรับลดสินค้า (เฉพาะเมื่อเลือกคลังแล้ว)
      if (updatedList[index].fWhId && parseCurrency(rawValue) > parseCurrency(onHand)) {
        getAlert("FAILED", "กรุณาระบุจำนวนไม่เกินคงเหลือ");
        updatedList[index].itemIncrease = null;
        updatedList[index].itemDecrease = null;
        updatedList[index].docType = null;
        updatedList[index].itemBalance = parseCurrency(onHand);
        setFormDetailList(updatedList);
        return;
      }

      updatedList[index].itemIncrease = null;
      updatedList[index].itemDecrease = value;
      updatedList[index].itemQty = parseCurrency(value);
      updatedList[index].itemBalance =
        parseCurrency(onHand) - parseCurrency(value);
    }

    updatedList[index].docType = type === "increase" ? "AI" : "AO";
    setFormDetailList(updatedList);
  };

  const handleFocus = (index, field) => {
    const updatedList = [...formDetailList];
    const raw = parseCurrency(updatedList[index][field]);
    // เก็บเป็น string ตัวเลขล้วน (ไม่มี comma) เพื่อให้ select ได้ครบ
    updatedList[index][field] = raw === 0 ? '0' : String(raw || '');
    setFormDetailList(updatedList);
  };

  const handleBlur = (index, field, value) => {
    const updatedList = [...formDetailList];
    const raw = parseFloat(String(value ?? '').replace(/,/g, ''));
    if (Number.isFinite(raw)) {
      const [intPart, decPart = '00'] = raw.toFixed(2).split('.');
      const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      updatedList[index][field] = `${intFmt}.${decPart}`;
    } else {
      updatedList[index][field] = '0.00';
    }
    setFormDetailList(updatedList);
  };

  // SET AP
  const [showApModal, setShowApModal] = useState(false);
  const handleApShow = () => setShowApModal(true);
  const handleApClose = () => setShowApModal(false);
  const onRowSelectAp = (apSelected) => {
    try {
      setFormMasterList({
        ...formMasterList,
        whDocCustId: apSelected.AP_Id,
        whDocCustCode: apSelected.AP_Code,
        whDocCustName: apSelected.AP_Name,
      });
      handleApClose(); // ปิด modal หลังจากเลือก
    } catch (error) {
      getAlert("FAILED", error);
    }
  };

  // SET ITEM
  const [showItemModal, setShowItemModal] = useState(false);
  const handleItemShow = () => setShowItemModal(true);
  const handleItemClose = () => setShowItemModal(false);
  const onRowSelectItem = (itemsSelected) => {
    try {
      // ใช้ Set เพื่อกรอง Item_Id ที่ซ้ำกัน
      const uniqueItems = Array.from(
        new Set(itemsSelected.map((item) => item.Item_Id))
      ).map((id) => {
        return itemsSelected.find((item) => item.Item_Id === id);
      });

      // อัปเดตรายการโดยคงค่าของสินค้าที่เคยมีอยู่
      const updatedFormDetailList = uniqueItems.map((itemSelected) => {
        // ค้นหาว่ามีสินค้าอยู่ใน formDetailList เดิมหรือไม่
        const existingItem = formDetailList.find(
          (item) => item.itemId === itemSelected.Item_Id
        );

        // หา default คลังจากประวัติล่าสุด หรือ auto-set ถ้ามีคลังเดียว
        const availableWhIds = [...new Set(
          allOnhandData
            .filter((oh) => Number(oh.Item_Id) === Number(itemSelected.Item_Id))
            .map((oh) => Number(oh.WH_Id))
        )];
        const globalWhId = selectedWarehouse ? Number(selectedWarehouse) : null;
        let defaultWhId =
          existingItem?.fWhId ||
          globalWhId ||
          latestWhPerItem[Number(itemSelected.Item_Id)] ||
          (availableWhIds.length === 1 ? availableWhIds[0] : null);

        const whOnhandEntry = defaultWhId
          ? allOnhandData.find(
              (oh) => Number(oh.Item_Id) === Number(itemSelected.Item_Id) && Number(oh.WH_Id) === defaultWhId
            )
          : null;
        const defaultOnhand = whOnhandEntry ? parseCurrency(whOnhandEntry.Item_Onhand) || 0 : 0;

        return {
          line: existingItem?.line || null,
          itemId: itemSelected.Item_Id,
          itemCode: itemSelected.Item_Code,
          itemName: itemSelected.Item_Name,
          itemQty: formatCurrency(0),
          itemIncrease: formatCurrency(0),
          itemDecrease: formatCurrency(0),
          itemUnit: existingItem ? existingItem.itemUnit : itemSelected.Item_Unit_ST,
          fWhId: defaultWhId,
          itemOnHand: defaultOnhand,
          itemBalance: defaultOnhand,
          itemPriceUnit: 0,
          itemTotal: 0,
          docType: null,
          itemIsReturn: null,
          itemReturnDate: null,
        };
      });

      setFormDetailList(updatedFormDetailList);
      handleItemClose();
    } catch (error) {
      getAlert("FAILED", error);
    }
  };
  const handleRemoveRow = (index) => {
    const newList = formDetailList.filter((_, i) => i !== index);
    setFormDetailList(newList);
  };

  const handleUploadAttachments = async (whDocId) => {
    const pendingFiles = selectedFiles.filter((file) => file.isPending);
    if (pendingFiles.length === 0) return;

    try {
      const filesWithBase64 = pendingFiles.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: file.dataUrl,
        isPending: true,
        displayName: file.name,
      }));

      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload-file`,
        {
          comp_id: localStorage.getItem("company"),
          doc_id: whDocId,
          DocCode_ID: 16, // รหัสประเภทเอกสาร ปรับตามระบบ
          upload_by: localStorage.getItem("name") || "system",
          file_status: "Y",
          files: filesWithBase64,
          doc_no_ref: null,
        },
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      if (response.data.status === "OK") {
        setSelectedFiles((prev) =>
          prev
            .filter((file) => !file.isPending)
            .concat(
              response.data.files.map((file) => ({
                F_Id: file.File_Id,
                fileName: file.fileName,
                File_Url: file.fileUrl,
                uploadDate: file.uploadDate,
                uploadBy: file.uploadBy,
              }))
            )
        );
        // getAlert("OK", "อัปโหลดไฟล์สำเร็จ");
      } else {
        throw new Error(response.data.message || "การอัปโหลดล้มเหลว");
      }
    } catch (error) {
      console.error("UPLOAD ERROR:", error);
      getAlert("FAILED", `การอัปโหลดไฟล์ล้มเหลว: ${error.message}`);
    }
  };

  const handleSubmit = async () => {
    try {
      // ทดสอบหน่วงเวลาก่อนบันทึก (ทดสอบป้องกันการซ้ำของ Doc_No)

      await randomDelay();

      const findMaxAdjNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '1' ORDER BY WHDoc_No DESC`
      );

      const maxAdj = getMaxAdjNo(findMaxAdjNo);
      let newMaxAdj = maxAdj;
      console.debug("New Max Adj No:", newMaxAdj);
      // ตรวจสอบว่า formDetailList มีค่าหรือมีความยาวเป็น 0
      if (!formDetailList || formDetailList.length === 0) {
        getAlert("FAILED", "กรุณาเพิ่มรายละเอียดสินค้าที่จะปรับปรุง");
        return;
      }

      // ตรวจสอบค่าภายใน formDetailList
      for (const item of formDetailList) {
        if (!item.fWhId) {
          getAlert("FAILED", `กรุณาเลือกคลังของสินค้า ${item.itemName}`);
          return;
        }
        if (!item.itemQty || parseCurrency(item.itemQty) === 0) {
          getAlert(
            "FAILED",
            `กรุณากรอกจำนวนปรับปรุงของสินค้า ${item.itemName}`
          );
          return;
        }
      }

      const formMasterData = {
        wh_doc_no: newMaxAdj,
        wh_doc_type: parseCurrency("1", 10),
        wh_doc_cust_id: parseCurrency(formMasterList.whDocCustId, 10),
        wh_doc_date: normalizeToSqlDateTime(formMasterList.whDocDate, "start"),
        wh_doc_trans_date: normalizeToSqlDateTime(
          formMasterList.whDocTransDate,
          "start"
        ),
        wh_doc_created_date: normalizeToSqlDateTime(formMasterList.whDocCreatedDate, "start"),
        wh_doc_created_by: formMasterList.whDocCreatedBy,
        wh_doc_status: parseCurrency("1", 10),
        wh_doc_seq: formatDateTime(new Date()),
        wh_doc_remark: formMasterList.whDocRemark,
        wh_doc_comp_id: formMasterList.whDocCompId,
        wh_doc_show_front: null,
        ref_doc_id: formMasterList.refDocId,
        f_wh_id:
          parseCurrency(formDetailList?.[0]?.fWhId, 10) ||
          parseCurrency(selectedWarehouse, 10) ||
          null,
      };

      // console.debug("Form Master Data:", formMasterData);

      // ส่งข้อมูลหลักไปยัง API
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/create-wh-doc-h`,
        formMasterData,
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      if (response.data.status === "OK") {
        // ลองใช้ WHDoc_Id จาก response.data ก่อน (ถ้ามี OUTPUT INSERTED)
        let whDocId = null;
        
        if (response.data.data && response.data.data.length > 0 && response.data.data[0].WHDoc_Id) {
          whDocId = parseCurrency(response.data.data[0].WHDoc_Id, 10);
          console.debug("Using WHDoc_Id from INSERT response:", whDocId);
        } else {
          // ถ้าไม่มีใน response ให้ query ใหม่
          const getWhDocIdResponse = await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/get-by-wh-doc-no`,
            {
              table: "WHDoc_H",
              wh_doc_no: formMasterData.wh_doc_no,
              comp_id: window.localStorage.getItem("company"),
            },
            {
              headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
            }
          );

          console.debug("getWhDocIdResponse:", getWhDocIdResponse);

          // รองรับทั้ง format เก่า (array) และ format ใหม่ ({ status: "OK", data: [...] })
          const responseData = Array.isArray(getWhDocIdResponse.data) 
            ? getWhDocIdResponse.data 
            : (getWhDocIdResponse.data?.data || getWhDocIdResponse.data || []);

          if (responseData && responseData.length > 0) {
            whDocId = parseCurrency(responseData[0].WHDoc_Id, 10);
            console.debug("Using WHDoc_Id from query response:", whDocId);
          }
        }

        if (whDocId) {

          const detailPromises = formDetailList.map(async (item, index) => {
            try {
              const formDetailData = {
                wh_doc_id: whDocId,
                line: index + 1,
                item_id: item.itemId,
                item_code: item.itemCode,
                item_name: item.itemName,
                item_qty: parseCurrency(item.itemQty),
                item_unit: item.itemUnit,
                item_price_unit: parseCurrency(item.itemPriceUnit),
                item_total: parseCurrency(item.itemTotal),
                doc_type: item.docType,
                f_wh_id: item.fWhId || null,
                f_zone_id: item.fZoneId,
                f_lt_id: item.fLtId,
                t_wh_id: item.tWhId,
                t_zone_id: item.fZoneId,
                t_lt_id: item.fLtId,
                ref_doc_id: item.refDocId,
                item_is_return: item.itemIsReturn,
                item_return_date: item.itemReturnDate,
              };

              // อัปเดตจำนวนสินค้าในคลัง
              await manageWhItemData(
                item.itemId,
                item.itemCode,
                item.itemName,
                item.fWhId || null,
                item.docType,
                parseCurrency(item.itemQty),
                whDocId,
                newMaxAdj,
                item.itemOnHand === 0 ? "เริ่มต้นประวัติคลัง" : newMaxAdj, 
                formMasterList.whDocRemark
              );

              // บันทึกรายละเอียดเอกสาร
              const detailResponse = await Axios.post(
                `${process.env.REACT_APP_API_URL}/api/create-wh-doc-d`,
                formDetailData,
                {
                  headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
                }
              );

              if (detailResponse.data.status !== "OK") {
                throw new Error(detailResponse.data.message || `การบันทึกรายละเอียดสินค้า ${item.itemName} ไม่สำเร็จ`);
              }

              return detailResponse;
            } catch (error) {
              console.error(`Error processing item ${item.itemName}:`, error);
              throw error;
            }
          });

          try {
            await Promise.all(detailPromises);
            await handleUploadAttachments(whDocId);
            getAlert("OK", "บันทึกใบปรับปรุงสินค้าสำเร็จ");
          } catch (error) {
            console.error("Error saving details:", error);
            getAlert("FAILED", error.message || "เกิดข้อผิดพลาดในการบันทึกรายละเอียดสินค้า");
            return;
          }
        } else {
          console.error("❌ Could not get WHDoc_Id after insert");
          getAlert("FAILED", "ไม่สามารถดึง WHDoc_Id หลังจากสร้างเอกสารได้");
          return;
        }

        const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
        if (originUrl) {
          localStorage.removeItem("TreasuryDocumentsOrigin");
          navigate(originUrl);
        } else {
          callInitialize();
        }
        // ไม่แสดง alert success หลังบันทึกสำเร็จ
      }
    } catch (error) {
      getAlert("FAILED", error.response?.data?.message || error.message);
    }
  };

  const handleUpdate = async () => {
    console.debug("Begin handleUpdate");
  };

  const handleCancel = async () => {
    try {
      // ข้อมูลหลักที่จะส่งไปยัง API
      const formMasterData = {
        wh_doc_no: formMasterList.whDocNo,
        wh_doc_status: parseCurrency("13", 10),
        comp_id: window.localStorage.getItem("company"),
      };

      // ส่งข้อมูลหลักไปยัง API
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/cancel-wh-doc-h`,
        formMasterData,
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
      if (originUrl) {
        localStorage.removeItem("TreasuryDocumentsOrigin");
        navigate(originUrl);
      } else {
        callInitialize();
      }
      getAlert(response.data.status, response.data.message);
    } catch (error) {
      getAlert("FAILED", error.response?.data?.message || error.message);
    }
  };

  return (
    <>
      <style>{`
        .form-adjust-page,
        .form-adjust-page label,
        .form-adjust-page span,
        .form-adjust-page td,
        .form-adjust-page h1,
        .form-adjust-page h2,
        .form-adjust-page h3,
        .form-adjust-page h4,
        .form-adjust-page h5,
        .form-adjust-page h6,
        .form-adjust-page p,
        .form-adjust-page div {
          color: #000 !important;
        }
        .form-adjust-page input,
        .form-adjust-page select,
        .form-adjust-page textarea {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
        .form-adjust-page .created-info-input {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
        .form-adjust-page .created-info-input:disabled {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
      `}</style>
      <Breadcrumbs
        page={whDocNo}
        items={[
          { name: "คลังสินค้า", url: "/Warehouse" },
          { name: "เอกสารงานคลัง", url: localStorage.getItem("TreasuryDocumentsOrigin") || "/treasury-documents" },
          { name: mode === "U" ? "เรียกดู" + name : "สร้าง" + name, url: "#" },
        ]}
      />
      <div
        className="body hidden-scrollbar form-adjust-page"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <div class="container-fluid mt-2 mb-4">
          <div className="row mb-1">
            <div className="col-4">
              <div className="d-flex">
                <label>วันที่เอกสาร : </label>
                <Datetime
                  className="input-spacing-input-date"
                  name="whDocDate"
                  value={parseThaiDateToMoment(formMasterList.whDocDate)}
                  onChange={(date) => handleChangeDateMaster(date, "whDocDate")}
                  dateFormat="DD-MM-YYYY"
                  timeFormat={false}
                  renderInput={(props, openCalendar) => (
                    <input
                      {...props}
                      className={`${props.className || ""} created-info-input`.trim()}
                      readOnly={true}
                      disabled={mode === "U"}
                      value={formatDateToThai(formMasterList.whDocDate)}
                      onClick={openCalendar}
                    />
                  )}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex">
                <label className="">วันที่สร้าง : </label>
                <input
                  type="text"
                  className="form-control input-spacing created-info-input"
                  value={formMasterList.whDocCreatedDate}
                  disabled={true}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex">
                <label>สร้างโดย : </label>
                <input
                  type="text"
                  className="form-control input-spacing created-info-input"
                  value={formMasterList.whDocCreatedBy}
                  disabled={true}
                />
              </div>
            </div>
          </div>
          <div className="row mb-1">
            <div className="col-4">
              <div className="d-flex">
                <label>หมายเหตุ : </label>
                <input
                  type="text"
                  className="form-control input-spacing"
                  name="whDocRemark"
                  value={formMasterList.whDocRemark || ""}
                  onChange={handleChangeMaster}
                  disabled={false}
                />
              </div>
            </div>
            <div className="col-4" />
          </div>
          <hr />
        </div>
        <div className="container-fluid my-3">
          <div className="row">
            <div className="col-12">
              <ItemWhTable
                mode={mode}
                showItemModal={showItemModal}
                handleItemClose={handleItemClose}
                itemDataList={itemDataList}
                onRowSelectItem={onRowSelectItem}
                formDetailList={formDetailList}
                handleItemShow={handleItemShow}
                handleQuantityChange={handleQuantityChange}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                handleRemoveRow={handleRemoveRow}
                formatCurrency={formatCurrency}
                disabled={false}
                whDataList={whDataList}
                allOnhandData={allOnhandData}
                onWhChangePerRow={handleWarehouseChangePerRow}
                globalWarehouse={selectedWarehouse}
                onGlobalWarehouseChange={handleGlobalWarehouseChange}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
              />
            </div>
          </div>
        </div>
      </div>
      <FormAction
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onCancel={handleCancel}
        mode={mode}
        disabled={false}
        footer={true}
        setMode={(m) => { if (m === "S") callInitialize(); }}
      />
    </>
  );
};

export default FormAdjust;
